import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Loader2 } from 'lucide-react';
import { getApiUrl, apiClient } from '../utils/api';
import { 
  getActivity,
  UpdateActivityData,
  getActivityTypes,
  getEmployeeActivityTypes,
  ActivityType,
  validateActivityDates
} from '../services';
import { DateInput } from '../components/DateInput';
import { TimeInput } from '../components/TimeInput';

export function ActivityEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [formData, setFormData] = useState<UpdateActivityData>({});
  const [shops, setShops] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [employeeActivityTypes, setEmployeeActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [shopId, setShopId] = useState('');

  useEffect(() => {
    if (id) {
      loadInitialData();
    }
  }, [id]);

  useEffect(() => {
    if (shopId) {
      loadEmployees();
    }
  }, [shopId]);

  const loadInitialData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const to = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      // We need shopId to get activity, so we'll need to get it from the activity
      // For now, let's get all shops and try to find the activity
      const [shopsRes, activityTypesRes, employeeTypesRes] = await Promise.all([
        fetch(getApiUrl('/api/admin/shops?limit=1000'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        getActivityTypes(),
        getEmployeeActivityTypes(),
      ]);

      const shopsData = await shopsRes.json();
      if (shopsData.success) {
        setShops(shopsData.data.shops || []);
      }
      setActivityTypes(activityTypesRes);
      setEmployeeActivityTypes(employeeTypesRes);

      // Try to find activity in each shop
      for (const shop of shopsData.data.shops || []) {
        try {
          const activity = await getActivity(id, shop.id, from, to);
          setShopId(shop.id);
          setFormData({
            title: activity.title,
            description: activity.description,
            colorOverride: activity.colorOverride,
            startDateTime: activity.startDateTime,
            endDateTime: activity.endDateTime,
            isAllDay: activity.isAllDay,
            isBlockingOverride: activity.isBlockingOverride,
            visibility: activity.visibility,
            status: activity.status,
            timezone: activity.timezone,
            employeeId: activity.employeeId,
            shopId: activity.shopId,
            activityTypeId: activity.activityTypeId ?? undefined,
            employeeActivityTypeId: activity.employeeActivityTypeId,
          });
          break;
        } catch (e) {
          // Continue to next shop
        }
      }
    } catch (error: any) {
      console.error('Échec du chargement de l\'activité:', error);
      setError('Échec du chargement des données de l\'activité');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    if (!shopId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/employees?shopId=${shopId}&limit=200`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEmployees(
          (data.data?.employees || []).map((e: any) => ({
            id: e.id,
            fullName:
              e.person?.displayName ||
              `${e.person?.firstName || ''} ${e.person?.lastName || ''}`.trim() ||
              e.person?.email ||
              'Employé',
          }))
        );
      }
    } catch (error) {
      console.error('Échec du chargement des employés:', error);
    }
  };

  const handleInputChange = (field: keyof UpdateActivityData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: 'startDateTime' | 'endDateTime', dateValue: string, timeValue: string) => {
    if (formData.isAllDay) {
      // For all-day, just use the date with time at midnight (ISO format)
      const dateTime = `${dateValue}T00:00:00.000Z`;
      handleInputChange(field, dateTime);
    } else {
      // Combine date and time, convert to ISO format
      const dateTime = `${dateValue}T${timeValue}:00.000Z`;
      handleInputChange(field, dateTime);
    }
  };

  const getSelectedActivityType = (): ActivityType | undefined => {
    return activityTypes.find(t => t.id === formData.activityTypeId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;

    if (!formData.employeeId) {
      setError('Veuillez sélectionner un employé');
      setErrorDetails([]);
      return;
    }

    if (!formData.activityTypeId) {
      setError('Veuillez sélectionner un type d\'activité');
      setErrorDetails([]);
      return;
    }

    if (formData.startDateTime && formData.endDateTime) {
      const validation = validateActivityDates(formData.startDateTime, formData.endDateTime);
      if (!validation.valid) {
        setError(validation.error || 'Dates invalides');
        setErrorDetails([]);
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      
      const response = await apiClient(`/api/planning/activities/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...formData,
          employeeActivityTypeId: formData.employeeActivityTypeId || undefined,
        }),
      });
      const data = await response.json();
      
      if (!data.success) {
        setError(data.message || 'Échec de la mise à jour de l\'activité');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
        return;
      }
      
      navigate('/planning/activities');
    } catch (error: any) {
      console.error('Échec de la mise à jour de l\'activité:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  const selectedType = getSelectedActivityType();
  const startDate = formData.startDateTime ? formData.startDateTime.split('T')[0] : '';
  const startTime = formData.startDateTime && !formData.isAllDay ? formData.startDateTime.split('T')[1]?.substring(0, 5) : '09:00';
  const endDate = formData.endDateTime ? formData.endDateTime.split('T')[0] : '';
  const endTime = formData.endDateTime && !formData.isAllDay ? formData.endDateTime.split('T')[1]?.substring(0, 5) : '18:00';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/planning/activities')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <Calendar className="w-6 h-6" />
              <span>Modifier l'Activité</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Informations Générales</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Boutique
                </label>
                <select
                  value={formData.shopId || ''}
                  onChange={(e) => {
                    setShopId(e.target.value);
                    handleInputChange('shopId', e.target.value);
                    handleInputChange('employeeId', '');
                  }}
                  className="input-field"
                >
                  <option value="">Sélectionner une boutique</option>
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Employé
                </label>
                <select
                  value={formData.employeeId || ''}
                  onChange={(e) => handleInputChange('employeeId', e.target.value)}
                  className="input-field"
                  disabled={!shopId}
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type d'Activité *
                </label>
                <select
                  value={formData.activityTypeId || ''}
                  onChange={(e) => handleInputChange('activityTypeId', e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Sélectionner un type</option>
                  {activityTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type d'Activité Employé (optionnel)
                </label>
                <select
                  value={formData.employeeActivityTypeId || ''}
                  onChange={(e) => handleInputChange('employeeActivityTypeId', e.target.value || null)}
                  className="input-field"
                >
                  <option value="">Aucun type employé</option>
                  {employeeActivityTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Titre
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Date et Heure</h2>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isAllDay || false}
                  onChange={(e) => handleInputChange('isAllDay', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-textPrimary">
                  Journée entière
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Date de Début
                </label>
                <DateInput
                  value={startDate}
                  onChange={(value) => handleDateChange('startDateTime', value, startTime)}
                  className="input-field"
                />
              </div>

              {!formData.isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Heure de Début
                  </label>
                  <TimeInput
                    value={startTime}
                    onChange={(value) => handleDateChange('startDateTime', startDate, value)}
                    className="input-field"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Date de Fin
                </label>
                <DateInput
                  value={endDate}
                  onChange={(value) => handleDateChange('endDateTime', value, endTime)}
                  className="input-field"
                />
              </div>

              {!formData.isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Heure de Fin
                  </label>
                  <TimeInput
                    value={endTime}
                    onChange={(value) => handleDateChange('endDateTime', endDate, value)}
                    className="input-field"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Options</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Bloquer les réservations
              </label>
              <select
                value={formData.isBlockingOverride === null ? 'default' : formData.isBlockingOverride ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value === 'default' ? null : e.target.value === 'true';
                  handleInputChange('isBlockingOverride', value);
                }}
                className="input-field"
              >
                <option value="default">
                  Par défaut ({selectedType?.isBlocking ? 'Oui' : 'Non'})
                </option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Visibilité
              </label>
              <select
                value={formData.visibility || 'INTERNAL'}
                onChange={(e) => handleInputChange('visibility', e.target.value as any)}
                className="input-field"
              >
                <option value="INTERNAL">Interne (backoffice uniquement)</option>
                <option value="FRONTOFFICE">Public (visible aux clients)</option>
                <option value="PRIVATE">Privé (praticien + admins)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Statut
              </label>
              <select
                value={formData.status || 'PLANNED'}
                onChange={(e) => handleInputChange('status', e.target.value as any)}
                className="input-field"
              >
                <option value="PLANNED">Planifié</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
              </select>
            </div>

            {selectedType && (
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Couleur (optionnel)
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.colorOverride || selectedType.defaultColor}
                    onChange={(e) => handleInputChange('colorOverride', e.target.value)}
                    className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.colorOverride || ''}
                    onChange={(e) => handleInputChange('colorOverride', e.target.value || null)}
                    className="input-field flex-1"
                    placeholder={selectedType.defaultColor}
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                  <button
                    type="button"
                    onClick={() => handleInputChange('colorOverride', null)}
                    className="btn-secondary text-sm"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/planning/activities')}
            className="btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Enregistrer les Modifications</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

